/**
 * Client-side mirror of the backend's `fee_notice_math.compute_fee_prefills`
 * (documents/services/fee_notice_math.py on the fee-notice backend stack).
 *
 * IMPORTANT: the backend does NOT recompute these numbers at write time —
 * `FeeNoticeConfirmView` stores whatever `fields` the reviewer submits
 * verbatim, and the Salesforce writer creates the Resolution record with
 * those exact values (see `fee_notice_sf_writer.write_fee_notice_to_salesforce`).
 * This module is therefore the arithmetic of record once
 * `FEE_NOTICE_SF_WRITE_ENABLED` is armed — keep it in lockstep with the
 * backend if the "Create Resolution" screen flow ever changes (the drift
 * sentinel `check_fee_notice_flow_drift` watches for that on the backend
 * side).
 *
 * Verified formulas (production Salesforce "Create Resolution" screen
 * flow, 301U100000GrearIAB, checked 2026-07-24):
 *
 *   admin fee default   = min(amount * fee% / 100 * 0.05, 100.00)
 *   gross attorney fee  = amount * fee% / 100 - admin fee
 *   referral fee % dflt = 25 when matter Source Type = 'Attorney Referral'
 *                         AND Source is set AND case type = 'VA Agency',
 *                         else 0
 *   fees due to others  = gross * referral% / 100
 *   fee due to G&P      = (gross - fees due to others) * 0.50, when the
 *                         matter is a Gordon & Partners matter
 *   net attorney fee    = gross - fees due to others - G&P fee
 *   fee due date        = resolution date + 70 days, VA Agency only
 *
 * No-fee letters (Matthew, 2026-07-24): fee% is set to 0 and everything
 * cascades to zero — a real entry, not a dismiss.
 */

export const VA_AGENCY_CASE_TYPE = "VA Agency";
export const ATTORNEY_REFERRAL_SOURCE_TYPE = "Attorney Referral";
export const FEE_DUE_DAYS_VA_AGENCY = 70;

const ADMIN_FEE_CAP = 100;
const DEFAULT_REFERRAL_FEE_PERCENT = 25;
const GP_SPLIT = 0.5;

/** Parses money/percent-ish input ("$1,234.56", "20", 20) to a finite
 * number, or null when the value is blank/unparseable. */
export const toNumber = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(String(value).replace(/[$,]/g, "").trim());
  return Number.isFinite(n) ? n : null;
};

// Rounds like the backend's Decimal(ROUND_HALF_UP).quantize("0.01") for the
// values this form deals in (never more than a few significant digits past
// the cent), and always renders two decimal places as a string so the
// fields match what Salesforce (and the reviewer) expect to see.
const money = (n) => {
  if (!Number.isFinite(n)) return "0.00";
  return (Math.round((n + Number.EPSILON) * 100) / 100).toFixed(2);
};

const addDaysIso = (isoDate, days) => {
  const d = new Date(`${isoDate}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return null;
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
};

/**
 * Derive the full fee section from Amount + Fee% + matter facts.
 *
 * `referralFeePercent` / `adminFee` are the two reviewer-overridable
 * inputs — pass `null` (or omit) to get the screen-flow default; pass a
 * value to keep the reviewer's manual edit "sticky" through further
 * recalculation (matches the backend's `compute_fee_prefills` contract:
 * these two are the only accepted overrides, everything else always
 * cascades).
 *
 * Returns the canonical field-key object shared with the confirm payload
 * (`amount`, `fee_percent`, `admin_fee`, `gross_attorney_fee`,
 * `referral_fee_percent`, `fees_due_to_others`, `fee_due_to_gordon_partners`,
 * `net_attorney_fee`, `fee_due_date`, `resolution_date`).
 */
/**
 * The Paul Green guard (2026-08-20): compare the letter's own "amount
 * withheld for fees" line against the fee section's arithmetic. The
 * stated line is VA's authoritative statement of what will actually be
 * wired — one live letter computed to $263.68 by the 20% arithmetic
 * while stating "$0.00" withheld, and no wire ever came.
 *
 * Matches against BOTH the raw fee (amount × fee%) and the
 * post-admin-deduction gross, since VA letters state either figure
 * depending on era. Returns null when there is nothing to compare
 * (no stated line, fee section not yet filled) or when the figures
 * agree; otherwise `{ stated, computed }` for the warning banner.
 */
export const checkWithheldLine = ({
  statedWithheld,
  amount,
  feePercent,
  grossAttorneyFee,
}) => {
  const stated = toNumber(statedWithheld);
  if (stated == null) return null;
  const amt = toNumber(amount);
  const pct = toNumber(feePercent);
  if (amt == null || pct == null) return null;

  const rawFee = (amt * pct) / 100;
  const gross = toNumber(grossAttorneyFee) ?? 0;
  const close = (a, b) => Math.abs(a - b) < 0.005;
  if (close(stated, rawFee) || close(stated, gross)) return null;
  return { stated, computed: rawFee };
};

export const computeFeePrefills = ({
  amount,
  feePercent,
  resolutionDate = null,
  caseType = "",
  gordonPartners = false,
  sourceType = "",
  hasSource = false,
  referralFeePercent = null,
  adminFee = null,
} = {}) => {
  const amt = toNumber(amount) ?? 0;
  const feePct = toNumber(feePercent) ?? 0;
  const isVaAgency = caseType === VA_AGENCY_CASE_TYPE;

  const resolvedReferralPercent =
    referralFeePercent != null
      ? toNumber(referralFeePercent) ?? 0
      : sourceType === ATTORNEY_REFERRAL_SOURCE_TYPE && hasSource && isVaAgency
      ? DEFAULT_REFERRAL_FEE_PERCENT
      : 0;

  const rawFee = (amt * feePct) / 100;
  const resolvedAdminFee =
    adminFee != null ? toNumber(adminFee) ?? 0 : Math.min(rawFee * 0.05, ADMIN_FEE_CAP);

  const gross = rawFee - resolvedAdminFee;
  const feesDueToOthers = (gross * resolvedReferralPercent) / 100;
  const gpFee = gordonPartners ? (gross - feesDueToOthers) * GP_SPLIT : 0;
  const net = gross - feesDueToOthers - gpFee;

  const feeDueDate =
    resolutionDate && isVaAgency ? addDaysIso(resolutionDate, FEE_DUE_DAYS_VA_AGENCY) : null;

  return {
    amount: money(amt),
    fee_percent: String(feePct),
    admin_fee: money(resolvedAdminFee),
    gross_attorney_fee: money(gross),
    referral_fee_percent: String(resolvedReferralPercent),
    fees_due_to_others: money(feesDueToOthers),
    fee_due_to_gordon_partners: money(gpFee),
    net_attorney_fee: money(net),
    fee_due_date: feeDueDate,
    resolution_date: resolutionDate || null,
  };
};
